import React from 'react';
import {
    Checkbox,
    DefaultButton,
    MessageBar,
    MessageBarType,
    PanelType,
    PrimaryButton,
    Separator,
    Stack,
    Text,
    useTheme,
} from '@fluentui/react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import PanelWrapper from '../Common/PanelWrapper';
import NumberField from '../Inputs/NumberField';
import MarkupTypeSelection from './MarkupTypeSelection';
import {
    useGetStorefrontProductCountQuery,
    useGetStorefrontsQuery,
} from '../../store/endpoints/StorefrontSettingsEndpoints';
import {useGetOrganizationEntitlementsQuery} from '../../store/endpoints/OrganizationsEndpoints';
import {RootState} from '../../store/rootReducer';
import {runBulkListOnStorefront} from '../../store/bulkOperationsSlice';
import {
    IBulkStorefrontEntrySettings,
    IBulkStorefrontListingRequest,
} from '../../models/BulkStorefront';
import {IToastAction} from '../Common/ToastAction';
import {storefrontProductsPath, STOREFRONT_PRODUCTS_TABS} from '../../logic/StorefrontRoutes';

interface IBulkStorefrontPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    catalog: string;
    productIds: string[];
    /** Called immediately after the bulk op is dispatched (e.g. to clear the caller's selection). */
    onApplied?: () => void;
}

const BulkStorefrontPanel: React.FC<IBulkStorefrontPanelProps> = (props) => {
    const {catalog, productIds} = props;
    const theme = useTheme();
    const dispatch = useDispatch();
    const history = useHistory();

    const org = useSelector((state: RootState) => state.settings.selectedOrganization);
    const storefronts = useGetStorefrontsQuery();
    const productCount = useGetStorefrontProductCountQuery();
    const entitlements = useGetOrganizationEntitlementsQuery(org ?? '', {skip: !org || org === ''});

    const [selectedStorefronts, setSelectedStorefronts] = React.useState<Set<string>>(new Set());
    const [entries, setEntries] = React.useState<{[id: string]: IBulkStorefrontEntrySettings}>({});
    const [featuredStorefronts, setFeaturedStorefronts] = React.useState<Set<string>>(new Set());
    const [productMarkup, setProductMarkup] = React.useState<number | undefined>();
    const [productMarkupType, setProductMarkupType] = React.useState<string | undefined>();
    const [includeAll, setIncludeAll] = React.useState<boolean>(false);
    const [includeAllTouched, setIncludeAllTouched] = React.useState<boolean>(false);
    const [overrideExisting, setOverrideExisting] = React.useState<boolean>(false);

    const toggleStorefront = (id: string, checked: boolean) => {
        setSelectedStorefronts(prev => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
        // Featuring only makes sense for included storefronts.
        if (!checked) {
            setFeaturedStorefronts(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const toggleFeatured = (id: string, checked: boolean) => {
        setFeaturedStorefronts(prev => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const updateEntry = (id: string, patch: Partial<IBulkStorefrontEntrySettings>) => {
        setEntries(prev => ({...prev, [id]: {...prev[id], ...patch}}));
    };

    const selectedIds = Array.from(selectedStorefronts);
    const canApply = selectedIds.length >= 1 || (includeAll && includeAllTouched);

    // Client-side entitlement warning (UX only — the server enforces the real limit).
    const currentCount = productCount.data?.count;
    const limit = entitlements.data?.storefrontProductLimit;
    const wouldExceedLimit =
        currentCount != null && limit != null && currentCount + productIds.length > limit;

    const buildRequest = (): IBulkStorefrontListingRequest => {
        const storefrontEntries: {[id: string]: IBulkStorefrontEntrySettings} = {};
        selectedIds.forEach(id => {
            const e = entries[id];
            if (!e) return;
            const cleaned: IBulkStorefrontEntrySettings = {};
            if (e.overrideMarkup != null) cleaned.overrideMarkup = e.overrideMarkup;
            if (e.overrideMarkupType && e.overrideMarkupType !== '') cleaned.overrideMarkupType = e.overrideMarkupType;
            if (e.minimumPrice != null) cleaned.minimumPrice = e.minimumPrice;
            if (Object.keys(cleaned).length > 0) storefrontEntries[id] = cleaned;
        });

        return {
            productIds,
            includedStorefronts: selectedIds,
            storefrontEntries: Object.keys(storefrontEntries).length > 0 ? storefrontEntries : undefined,
            featuredStorefronts: selectedIds.filter(id => featuredStorefronts.has(id)),
            // Tri-state: only ever send true (user opted in) or null (leave alone). Never false.
            includeInAllStorefronts: includeAll && includeAllTouched ? true : null,
            storefrontOverrideMarkup: productMarkup ?? null,
            storefrontOverrideMarkupType:
                productMarkupType && productMarkupType !== '' ? productMarkupType : null,
            overrideExisting,
        };
    };

    const apply = () => {
        const request = buildRequest();
        const label = `Listing ${productIds.length} products on ${selectedIds.length} storefront(s)…`;
        // Only offer the follow-up link when there's one unambiguous storefront to send them to.
        const action: IToastAction | undefined = selectedIds.length === 1
            ? {
                label: 'View storefront products',
                onClick: () => history.push(storefrontProductsPath(selectedIds[0], STOREFRONT_PRODUCTS_TABS.products)),
            }
            : undefined;
        dispatch(runBulkListOnStorefront(catalog, request, label, action) as any);
        props.onApplied?.();
        props.onDismiss();
    };

    const footerButtons = [
        <PrimaryButton
            key="apply"
            text={`Apply to ${productIds.length} Products`}
            iconProps={{iconName: 'Save'}}
            disabled={!canApply}
            onClick={apply}
        />,
        <DefaultButton key="cancel" text="Cancel" onClick={() => props.onDismiss()} />,
    ];

    const denseStackTokens = {childrenGap: '0.5em'};

    return (
        <PanelWrapper
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            title="List on Storefront"
            type={PanelType.large}
            isFooterAtBottom
            footerButtons={footerButtons}
        >
            <Stack tokens={{childrenGap: '1em'}} styles={{root: {padding: '1em 0'}}}>
                <Text variant="small" styles={{root: {color: theme.palette.neutralSecondary}}}>
                    Apply storefront listing settings to {productIds.length} selected product
                    {productIds.length === 1 ? '' : 's'}.
                </Text>

                {wouldExceedLimit &&
                    <MessageBar messageBarType={MessageBarType.warning}>
                        This batch of {productIds.length} products would bring your storefront listings
                        ({currentCount}) past your limit of {limit}. The server may reject the batch.
                    </MessageBar>}

                <Stack tokens={{childrenGap: '0.5em'}}>
                    <h3 style={{margin: 0}}>Storefronts</h3>
                    <table className="data-table">
                        <thead>
                        <tr>
                            <th>Include</th>
                            <th>Storefront</th>
                            <th>Override Markup</th>
                            <th>Markup Type</th>
                            <th>Minimum Price</th>
                            <th>Featured</th>
                        </tr>
                        </thead>
                        <tbody>
                        {storefronts.data?.items?.map(sf => {
                            const id = sf.id ?? '';
                            const checked = selectedStorefronts.has(id);
                            const entry = entries[id] ?? {};
                            return (
                                <tr key={id}>
                                    <td style={{textAlign: 'center'}}>
                                        <Checkbox
                                            checked={checked}
                                            ariaLabel={`Include ${sf.name ?? id}`}
                                            onChange={(_e, v) => toggleStorefront(id, v ?? false)}
                                        />
                                    </td>
                                    <td>{sf.name ?? id}</td>
                                    <td>
                                        <NumberField
                                            value={entry.overrideMarkup}
                                            decimal
                                            disabled={!checked}
                                            onChange={(v) => updateEntry(id, {overrideMarkup: v})}
                                        />
                                    </td>
                                    <td>
                                        <MarkupTypeSelection
                                            value={entry.overrideMarkupType}
                                            disabled={!checked}
                                            onChange={(v) => updateEntry(id, {overrideMarkupType: v})}
                                        />
                                    </td>
                                    <td>
                                        <NumberField
                                            prefix="$"
                                            value={entry.minimumPrice}
                                            decimal
                                            disabled={!checked}
                                            onChange={(v) => updateEntry(id, {minimumPrice: v})}
                                        />
                                    </td>
                                    <td style={{textAlign: 'center'}}>
                                        <Checkbox
                                            checked={featuredStorefronts.has(id)}
                                            disabled={!checked}
                                            ariaLabel={`Feature on ${sf.name ?? id}`}
                                            onChange={(_e, v) => toggleFeatured(id, v ?? false)}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </Stack>

                <Separator />

                <Stack tokens={{childrenGap: '0.5em'}}>
                    <h3 style={{margin: 0}}>Product Default Markup</h3>
                    <Stack horizontal wrap tokens={denseStackTokens}>
                        <div style={{width: 160}}>
                            <NumberField
                                label="Markup"
                                value={productMarkup}
                                decimal
                                onChange={setProductMarkup}
                            />
                        </div>
                        <div style={{width: 180}}>
                            <MarkupTypeSelection
                                label="Markup Type"
                                value={productMarkupType}
                                onChange={setProductMarkupType}
                            />
                        </div>
                    </Stack>
                </Stack>

                <Separator />

                <Stack tokens={{childrenGap: '0.5em'}}>
                    <Checkbox
                        label="Include in All Storefronts"
                        checked={includeAll}
                        onChange={(_e, v) => {
                            setIncludeAll(v ?? false);
                            setIncludeAllTouched(true);
                        }}
                    />
                    <Checkbox
                        label="Override Existing Storefront Settings"
                        checked={overrideExisting}
                        onChange={(_e, v) => setOverrideExisting(v ?? false)}
                    />
                    <Text variant="small" styles={{root: {color: theme.palette.neutralSecondary}}}>
                        When off, products already on a storefront keep their current markup; new
                        storefronts are still added.
                    </Text>
                </Stack>
            </Stack>
        </PanelWrapper>
    );
};

export default BulkStorefrontPanel;
